import { useEffect,useMemo,useState } from "react";
import {
  Box,Button,Checkbox,CircularProgress,Table,TableBody,TableCell,
  TableContainer,TableHead,TableRow,Typography
} from "@mui/material";
import { useParams,useSearchParams } from "react-router-dom";
import { useAppTheme } from "../../../context/ThemeContext";
import { useAlert } from "../../../context/AlertContext";

const readResponse=async response=>{
  const contentType=response.headers.get("content-type")||"";
  if(contentType.includes("application/json")) return response.json();
  if(!response.ok) throw new Error("El servidor no respondió correctamente.");
  return {};
};

const getCookie=name=>{
  const value=`; ${document.cookie}`;
  const parts=value.split(`; ${name}=`);
  if(parts.length===2) return decodeURIComponent(parts.pop().split(";").shift());
  return "";
};

export default function AdminTeamPlayers(){
  const {teamSlug}=useParams();
  const [searchParams]=useSearchParams();
  const {colors,typography,shape}=useAppTheme();
  const {showError}=useAlert();

  const category=searchParams.get("category")||"";

  const [loading,setLoading]=useState(true);
  const [downloading,setDownloading]=useState(false);
  const [team,setTeam]=useState(null);
  const [categoryData,setCategoryData]=useState(null);
  const [players,setPlayers]=useState([]);
  const [selected,setSelected]=useState([]);

  const loadPlayers=async()=>{
    if(!teamSlug||!category){
      setLoading(false);
      setPlayers([]);
      setTeam(null);
      setCategoryData(null);
      setSelected([]);
      return;
    }

    try{
      setLoading(true);

      const params=new URLSearchParams();
      params.set("team",teamSlug);
      params.set("category",category);

      const response=await fetch(
        `/competition/admin/players/?${params.toString()}`,
        {credentials:"include"}
      );

      const data=await readResponse(response);

      if(!response.ok){
        throw new Error(data.error||"No se pudieron cargar los jugadores.");
      }

      const results=Array.isArray(data.results)?data.results:[];

      setPlayers(results);
      setSelected([]);

      const firstPlayer=results[0]||null;

      setTeam(
        firstPlayer?.team||{
          name:teamSlug,
          slug:teamSlug
        }
      );

      const currentRegistration=(firstPlayer?.categories||[]).find(
        item=>(item.category?.slug||item.slug)===category
      );

      setCategoryData(
        currentRegistration?.category||{
          name:category,
          slug:category
        }
      );
    }catch(error){
      setPlayers([]);
      setSelected([]);
      setTeam(null);
      setCategoryData(null);
      showError(error.message||"No se pudieron cargar los jugadores.");
    }finally{
      setLoading(false);
    }
  };

  useEffect(()=>{
    loadPlayers();
  },[teamSlug,category]);

  const playerKey=player=>player.slug;

  const selectablePlayers=useMemo(
    ()=>players.filter(player=>player.active!==false),
    [players]
  );

  const allSelected=useMemo(
    ()=>selectablePlayers.length>0&&selectablePlayers.every(
      player=>selected.includes(playerKey(player))
    ),
    [selectablePlayers,selected]
  );

  const someSelected=useMemo(
    ()=>selectablePlayers.some(
      player=>selected.includes(playerKey(player))
    )&&!allSelected,
    [selectablePlayers,selected,allSelected]
  );

  const togglePlayer=player=>{
    if(player.active===false) return;

    const key=playerKey(player);

    setSelected(current=>
      current.includes(key)
        ?current.filter(item=>item!==key)
        :[...current,key]
    );
  };

  const toggleAll=()=>{
    if(allSelected){
      setSelected([]);
      return;
    }

    setSelected(
      selectablePlayers.map(player=>playerKey(player))
    );
  };

  const getOtherCategories=player=>{
    const values=(player.categories||[])
      .filter(item=>(item.category?.slug||item.slug)!==category)
      .map(item=>item.category?.name||item.name)
      .filter(Boolean);

    return [...new Set(values)];
  };

  const getJersey=player=>{
    const registration=(player.categories||[]).find(
      item=>(item.category?.slug||item.slug)===category
    );

    return registration?.jersey_number??player.jersey_number??null;
  };

  const handleBulkDownload=async()=>{
    if(!selected.length){
      showError("Selecciona al menos un jugador.");
      return;
    }

    try{
      setDownloading(true);

      const response=await fetch(
        `/competition/teams/${encodeURIComponent(teamSlug)}/carnets/`,
        {
          method:"POST",
          credentials:"include",
          headers:{
            "Content-Type":"application/json",
            "X-CSRFToken":getCookie("csrftoken")
          },
          body:JSON.stringify({
            category,
            players:[...new Set(selected)]
          })
        }
      );

      if(!response.ok){
        const contentType=response.headers.get("content-type")||"";
        let message="No se pudieron generar los carnets.";

        if(contentType.includes("application/json")){
          const data=await response.json();
          message=data.error||message;
        }

        throw new Error(message);
      }

      const blob=await response.blob();
      const url=window.URL.createObjectURL(blob);
      const link=document.createElement("a");

      link.href=url;
      link.download=`carnets-${teamSlug}-${category}.pdf`;

      document.body.appendChild(link);
      link.click();
      link.remove();

      window.URL.revokeObjectURL(url);
    }catch(error){
      showError(error.message||"No se pudieron generar los carnets.");
    }finally{
      setDownloading(false);
    }
  };

  if(!category){
    return (
      <Box
        sx={{
          py:5,
          textAlign:"center",
          border:`1px solid ${colors.border}`,
          borderRadius:shape.radiusLarge,
          backgroundColor:colors.surface
        }}
      >
        <Typography
          sx={{
            fontSize:typography.sizes.md,
            color:colors.textSecondary
          }}
        >
          Selecciona primero una categoría desde el listado de equipos.
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box
        sx={{
          mb:1.5,
          display:"flex",
          flexDirection:{xs:"column",sm:"row"},
          alignItems:{xs:"stretch",sm:"center"},
          justifyContent:"space-between",
          gap:1.5
        }}
      >
        <Box>
          <Typography
            sx={{
              fontSize:typography.sizes.xl,
              fontWeight:700,
              color:colors.text
            }}
          >
            {team?.name||"Jugadores"}
          </Typography>

          <Typography
            sx={{
              mt:0.3,
              fontSize:typography.sizes.sm,
              color:colors.textSecondary
            }}
          >
            {categoryData?.name||category}
          </Typography>
        </Box>

        <Button
          size="small"
          variant="contained"
          disabled={!selected.length||downloading}
          onClick={handleBulkDownload}
          sx={{
            alignSelf:{xs:"flex-start",sm:"center"},
            px:2,
            backgroundColor:colors.secondary,
            color:colors.tableHeaderText,
            boxShadow:"none",
            textTransform:"none",
            "&:hover":{
              backgroundColor:colors.secondaryHover,
              boxShadow:"none"
            }
          }}
        >
          {downloading
            ?"Generando..."
            :`Descargar carnets (${selected.length})`
          }
        </Button>
      </Box>

      <Box
        sx={{
          mb:1.5,
          px:1.5,
          py:1.2,
          border:`1px solid ${colors.border}`,
          borderRadius:shape.radius,
          backgroundColor:colors.backgroundSoft
        }}
      >
        <Typography
          sx={{
            fontSize:typography.sizes.sm,
            fontWeight:600,
            color:colors.text
          }}
        >
          Un carnet por jugador
        </Typography>

        <Typography
          sx={{
            mt:0.25,
            fontSize:typography.sizes.xs,
            lineHeight:1.5,
            color:colors.textSecondary
          }}
        >
          Algunos jugadores pueden estar inscritos en varias categorías. El carnet pertenece al jugador, no a la categoría. Si un jugador participa en otras categorías se indica en esta lista para evitar imprimir su carnet más de una vez.
        </Typography>
      </Box>

      <TableContainer
        sx={{
          border:`1px solid ${colors.border}`,
          borderRadius:shape.radiusLarge,
          backgroundColor:colors.surface,
          overflowX:"auto"
        }}
      >
        <Table size="small" sx={{minWidth:850}}>
          <TableHead>
            <TableRow
              sx={{
                "& .MuiTableCell-root":{
                  backgroundColor:colors.tableHeader,
                  color:colors.tableHeaderText,
                  borderBottom:`1px solid ${colors.tableHeader}`,
                  fontSize:typography.sizes.sm,
                  fontWeight:700
                }
              }}
            >
              <TableCell padding="checkbox">
                <Checkbox
                  size="small"
                  checked={allSelected}
                  indeterminate={someSelected}
                  disabled={!selectablePlayers.length}
                  onChange={toggleAll}
                  sx={{
                    color:colors.tableHeaderText,
                    "&.Mui-checked":{
                      color:colors.tableHeaderText
                    },
                    "&.MuiCheckbox-indeterminate":{
                      color:colors.tableHeaderText
                    }
                  }}
                />
              </TableCell>

              <TableCell>Jugador</TableCell>
              <TableCell>Identificación</TableCell>
              <TableCell>Dorsal</TableCell>
              <TableCell>Otras categorías</TableCell>
              <TableCell>Estado</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {loading&&(
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{py:5}}>
                  <CircularProgress
                    size={24}
                    sx={{color:colors.secondary}}
                  />
                </TableCell>
              </TableRow>
            )}

            {!loading&&!players.length&&(
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{py:5}}>
                  <Typography
                    sx={{
                      fontSize:typography.sizes.md,
                      color:colors.textSecondary
                    }}
                  >
                    No existen jugadores inscritos en esta categoría.
                  </Typography>
                </TableCell>
              </TableRow>
            )}

            {!loading&&players.map(player=>{
              const key=playerKey(player);
              const otherCategories=getOtherCategories(player);
              const isSelected=selected.includes(key);
              const jersey=getJersey(player);
              const active=player.active!==false;

              return (
                <TableRow
                  key={key}
                  hover
                  sx={{
                    opacity:active?1:0.65,
                    "&:last-child td":{
                      borderBottom:0
                    }
                  }}
                >
                  <TableCell padding="checkbox">
                    <Checkbox
                      size="small"
                      disabled={!active}
                      checked={isSelected}
                      onChange={()=>togglePlayer(player)}
                      sx={{
                        color:colors.controlInactive,
                        "&.Mui-checked":{
                          color:colors.controlActive
                        }
                      }}
                    />
                  </TableCell>

                  <TableCell>
                    <Box
                      sx={{
                        display:"flex",
                        alignItems:"center",
                        gap:1.2
                      }}
                    >
                      {player.photo&&(
                        <Box
                          component="img"
                          src={player.photo}
                          alt={player.name}
                          sx={{
                            width:36,
                            height:48,
                            flexShrink:0,
                            objectFit:"cover",
                            borderRadius:shape.radiusSmall,
                            border:`1px solid ${colors.border}`
                          }}
                        />
                      )}

                      <Box sx={{minWidth:0}}>
                        <Typography
                          sx={{
                            fontSize:typography.sizes.sm,
                            fontWeight:600,
                            color:colors.text
                          }}
                        >
                          {player.name}
                        </Typography>

                        {player.age!==undefined&&player.age!==null&&(
                          <Typography
                            sx={{
                              fontSize:typography.sizes.xs,
                              color:colors.textSecondary
                            }}
                          >
                            {player.age} años
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </TableCell>

                  <TableCell>
                    <Typography
                      sx={{
                        fontSize:typography.sizes.sm,
                        color:colors.text
                      }}
                    >
                      {player.identification||"—"}
                    </Typography>
                  </TableCell>

                  <TableCell>
                    <Typography
                      sx={{
                        fontSize:typography.sizes.sm,
                        fontWeight:600,
                        color:colors.text
                      }}
                    >
                      {jersey!==null&&jersey!==undefined
                        ?`#${jersey}`
                        :"—"
                      }
                    </Typography>
                  </TableCell>

                  <TableCell>
                    {otherCategories.length?(
                      <Box>
                        <Typography
                          sx={{
                            fontSize:typography.sizes.sm,
                            fontWeight:600,
                            color:colors.text
                          }}
                        >
                          También inscrito
                        </Typography>

                        <Typography
                          sx={{
                            mt:0.15,
                            fontSize:typography.sizes.xs,
                            color:colors.textSecondary
                          }}
                        >
                          {otherCategories.join(", ")}
                        </Typography>
                      </Box>
                    ):(
                      <Typography
                        sx={{
                          fontSize:typography.sizes.sm,
                          color:colors.textSecondary
                        }}
                      >
                        —
                      </Typography>
                    )}
                  </TableCell>

                  <TableCell>
                    <Typography
                      sx={{
                        fontSize:typography.sizes.sm,
                        fontWeight:600,
                        color:active
                          ?colors.success
                          :colors.textSecondary
                      }}
                    >
                      {active?"Activo":"Inactivo"}
                    </Typography>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}